import { useMemo } from "react";
import { filter, keyBy } from "lodash";
import { useSelector } from "react-redux";
import { getCurrentDisabledFormFields } from "../reducers/orgUnit.reducers";

/**
 * Custom hook to get and transform the disabled form fields from Redux state into a map for easier lookup
 *
 * Business Logic:
 * - The backend (OrgUnitController: GetDetail()) sends DisabledFormFields as an array of objects with FormType and
 * FieldName properties
 * - These disabled fields represent form fields that should be hidden from the UI across different forms
 * - Each disabled field belongs to a specific form type (e.g., NotificationConfigurationFormField)
 * - We need to filter for the relevant form type and create a lookup map for efficient field checking
 *
 * @param {string} formType - The type of form to filter disabled fields for (e.g., "NotificationConfigurationFormField")
 *
 * @returns {Object} A map where keys are fieldNames and values are the disabled field objects
 *                  This allows for O(1) lookup when checking if a specific field is disabled
 */
export default function useDisabledFormFields(formType) {
  // This matches the "FieldName" property sent from TranslateDisabledFormFieldsToViewModel in OrgUnitController: GetDetail()
  const FIELD_NAME_PROPERTY = "fieldName";

  // Get disabled form fields directly from Redux state
  const disabledFormFields = useSelector(getCurrentDisabledFormFields);

  return useMemo(() => {
    // If no disabled fields or formType provided, return empty object
    if (!disabledFormFields || !formType) return {};

    // Filter to only include fields of the specific configuration form type
    const relevantFields = filter(disabledFormFields, field => field.formType === formType);

    // Convert the filtered array to a map object with fieldName as the key
    // This makes it easy to check if a specific field is disabled with: disabledFieldsMap[fieldName]
    return keyBy(relevantFields, FIELD_NAME_PROPERTY);
  }, [disabledFormFields, formType]);
}
